// @flow
import React, {Component} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import classNames from 'classnames';
import {logExperiment} from '@homeaway/abacus-client-utils';
import Hit, {fragments as HitFragment} from '../Hit/Hit';
import PinnedProperty, {
    fragments as PinnedPropertyFragment
} from '../PinnedProperty/PinnedProperty';
import {FormattedMessage} from 'react-intl';
import gql from 'graphql-tag';
import {edapTriggerPriceSummaryDisplay} from '@homeaway/react-price-summary';
import {Waypoint} from '@homeaway/react-unveil';
import {trigger as edapTrigger, GENERIC_EVENT} from '../../utils/edapWrapper';
import type {Shape as HitShape} from '../Hit/Hit';
import withTiming from '../Timing/withTiming';
import withHitLoadTime from '../Timing/withHitLoadTime';
import ScrollListener from '../ScrollListener/ScrollListener';
import {isDifferent} from '../../utils';

import type {Shape as PinnedPropertyShape} from '../PinnedProperty/PinnedProperty';
import type {State as ReduxState} from '../../reducers';
import type {
    AbacusEvaluationData,
    State as AbacusState
} from '../../reducers/abacus';
import withPAR from '../Timing/withPAR';
import {SERP_INLINE_ADS} from '../../constants/ads';
import {
    HA_SERP_PRICE_SUMMARY_FF,
    HA_ADS_HOLDBACK_SERP_AND_PDP,
    HA_URGENCY_HITCOUNT_MESSAGE_SERVICE_SCARCITY,
    HA_ADS_NATIVE_UNIT_SERP_DESKTAB,
    HA_ADS_NATIVE_UNIT_SERP_MOBILE
} from '../../abacus/experiments';
import {VRBO} from '../../constants/sites';
import {triggerListHitClickEDAPEvent} from '../../utils/hitEdapUtils';

const inlineAdPositions = [5, 15, 25];

export const fragments = {
    hitCollection: gql`
        fragment ResultsHitCollection on HitCollection {
            isDatelessSearch
            hits {
                ...PinnedPropertyHit
                ...HitSERPHit
            }
        }
        ${PinnedPropertyFragment.PinnedPropertyHit}
        ${HitFragment.SERPHit}
    `
};

type StateProps = {
    fetchImages?: string,
    priceSummaryFeatureFlagEvaluationData?: AbacusEvaluationData | null,
    isAdsHoldBack: boolean,
    siteName: string,
    isMobile: boolean,
    urgencyMessagingFeatureFlagEvaluationData?: AbacusEvaluationData | null,
    abacus: AbacusState
};

export type HitCollectionHitShape = HitShape & PinnedPropertyShape;

export type Props = {
    hitcollection: {
        isDatelessSearch: boolean,
        hits: HitCollectionHitShape[]
    },
    onScrollHitEnter: (number) => void,
    onHitMouseEnter: (string) => void,
    onHitMouseLeave: () => void
} & StateProps;

export const NoResults = () => (
    <div className="NoResults">
        <FormattedMessage
            id="noresults"
            defaultMessage="There are no results which match your criteria. Try refining your search parameters."
        />
    </div>
);
NoResults.displayName = 'NoResults';

const hasNoPrice = (hit: HitCollectionHitShape): 1 | 0 => {
    return hit && !(hit.price && hit.price.currencyUnits && hit.price.value)
        ? 1
        : 0;
};

const onHitClick = (hit: HitShape) => {
    const options = {delayProcessing: true};

    triggerListHitClickEDAPEvent(hit);

    if (hit.priceSummary) {
        edapTriggerPriceSummaryDisplay(hit.priceSummary, options);
    }
};

export const checkPriceless = (hits: HitCollectionHitShape[]) => {
    if (!hits) {
        return;
    }
    let numPriceless = 0;
    hits.map((h: HitCollectionHitShape) => {
        numPriceless += hasNoPrice(h);
    });
    const pricelesslabel: string = numPriceless.toString();
    if (numPriceless > 0) {
        edapTrigger(GENERIC_EVENT, {
            eventaction: 'no price presented',
            highfrequency: 'true',
            eventlabel: pricelesslabel
        });
    }
};

let loggedPriceSummaryFeatureFlag = false;
let loggedUrgencyMessagingFeatureFlag = false;

/**
 * Only used to reset the logged flag in unit tests
 */
export function resetLoggedPriceSummaryFeatureFlag() {
    loggedPriceSummaryFeatureFlag = false;
}

export function resetLoggedUrgencyMessagingFeatureFlag() {
    loggedUrgencyMessagingFeatureFlag = false;
}

export class HitCollection extends Component<Props> {
    constructor(props: Props) {
        super(props);
        const {
            hitcollection: {hits}
        } = props;
        checkPriceless(hits);
    }

    componentDidMount() {
        if (
            this.props.priceSummaryFeatureFlagEvaluationData &&
            !loggedPriceSummaryFeatureFlag
        ) {
            logExperiment(this.props.priceSummaryFeatureFlagEvaluationData);
            loggedPriceSummaryFeatureFlag = true;
        }
        if (
            this.props.urgencyMessagingFeatureFlagEvaluationData &&
            !loggedUrgencyMessagingFeatureFlag
        ) {
            logExperiment(this.props.urgencyMessagingFeatureFlagEvaluationData);
            loggedUrgencyMessagingFeatureFlag = true;
        }
    }

    componentDidUpdate(prevProps: Props) {
        const {
            hitcollection: {hits: prevHits}
        } = prevProps;
        const {
            hitcollection: {hits}
        } = this.props;

        if (prevHits && hits && isDifferent(hits, prevHits)) {
            checkPriceless(hits);
        }
    }

    render() {
        const {
            hitcollection: {hits, isDatelessSearch},
            onScrollHitEnter,
            onHitMouseEnter,
            onHitMouseLeave,
            siteName,
            isAdsHoldBack,
            isMobile,
            abacus
        } = this.props;

        const handleEnter = (label: number) => {
            onScrollHitEnter(label);
        };

        if (!hits || !hits.length) {
            return <NoResults />;
        }

        const handleScroll = () => handleEnter(0);

        const nativeAds =
            abacus &&
            ((abacus[HA_ADS_NATIVE_UNIT_SERP_DESKTAB] &&
                abacus[HA_ADS_NATIVE_UNIT_SERP_DESKTAB].value === 1) ||
                (isMobile &&
                    abacus[HA_ADS_NATIVE_UNIT_SERP_MOBILE] &&
                    abacus[HA_ADS_NATIVE_UNIT_SERP_MOBILE].value === 1));

        const displayInlineAds =
            hits.length >= 7 &&
            (siteName !== VRBO || nativeAds) &&
            !isAdsHoldBack;

        const inlineAdsClasses = classNames('inline__ad', {
            native__ad: nativeAds && !isAdsHoldBack
        });

        return (
            <ScrollListener onScroll={handleScroll} data-wdio="ScrollListener">
                <div className="HitCollection" data-wdio="HitCollection">
                    {hits.map((hit, index) => {
                        const hitProps = {
                            key: hit.listingId,
                            hit,
                            isDatelessSearch,
                            onHitClick,
                            onMouseEnter: onHitMouseEnter,
                            onMouseLeave: onHitMouseLeave
                        };
                        if (hit.pinned) {
                            return (
                                <PinnedProperty
                                    key={index}
                                    available={hit.available}
                                    numResults={hits.length}
                                >
                                    <Hit {...hitProps} />
                                </PinnedProperty>
                            );
                        }
                        if (
                            displayInlineAds &&
                            inlineAdPositions.includes(index)
                        ) {
                            return (
                                <div key={index}>
                                    <div
                                        id={
                                            SERP_INLINE_ADS[
                                                inlineAdPositions.indexOf(index)
                                            ]
                                        }
                                        className={inlineAdsClasses}
                                    />
                                    <Hit {...hitProps} />
                                </div>
                            );
                        }
                        if ([4, 9].includes(index)) {
                            return (
                                <Waypoint
                                    data-wdio={`Waypoint${index + 1}`}
                                    key={index}
                                    label={index + 1}
                                    onEnter={handleEnter}
                                >
                                    <Hit {...hitProps} />
                                </Waypoint>
                            );
                        }
                        return <Hit {...hitProps} />;
                    })}
                </div>
            </ScrollListener>
        );
    }
}

const mapStateToProps = (state: ReduxState): StateProps => {
    let {
        abacus,
        searchParams,
        system: {siteName, isMobile}
    } = state;
    let hasDates =
        searchParams &&
        searchParams.params &&
        searchParams.params.arrival &&
        searchParams.params.departure;

    return {
        abacus,
        isAdsHoldBack:
            (abacus &&
                abacus[HA_ADS_HOLDBACK_SERP_AND_PDP] &&
                (abacus[HA_ADS_HOLDBACK_SERP_AND_PDP].value === 1 ||
                    abacus[HA_ADS_HOLDBACK_SERP_AND_PDP].value === 2)) ||
            false,
        fetchImages:
            searchParams &&
            searchParams.params &&
            searchParams.params.fetchImages,
        priceSummaryFeatureFlagEvaluationData:
            hasDates && abacus && !!abacus[HA_SERP_PRICE_SUMMARY_FF]
                ? abacus[HA_SERP_PRICE_SUMMARY_FF]
                : null,
        siteName,
        isMobile,
        urgencyMessagingFeatureFlagEvaluationData:
            abacus && !!abacus[HA_URGENCY_HITCOUNT_MESSAGE_SERVICE_SCARCITY]
                ? abacus[HA_URGENCY_HITCOUNT_MESSAGE_SERVICE_SCARCITY]
                : null
    };
};

export default compose(
    connect(mapStateToProps),
    withTiming('hitCollection'),
    withHitLoadTime,
    withPAR
)(HitCollection);
